"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import RecurrenceScopeSelector from "@/components/custom-calendar/dialogs/components/recurrence-scope-selector";
import { Button } from "@/components/ui";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserContext } from "@/contexts/user-context";
import { useEventTitle } from "@/hooks/use-event-title";
import { getFeeStatement } from "@/lib/appointment-fee-statement";
import { formatCurrency } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { RecurrenceModificationMode } from "@/types/enums/recurrence-modification-mode";
import { AppointmentFees, EventTiny } from "@/types/event";
import { useAppointmentNotificationStore } from "@/stores/appointment-notification-store";

/**
 * Slim event subset the cancellation modal needs. A `Pick` on the canonical
 * `EventTiny` type lets callers pass their event objects directly — any
 * `EventTiny` / `EventDetail` is assignable here, and lighter shapes that
 * simply lack `recurringEventId` are still structurally compatible.
 */
type CancellationFeeService = {
  id?: string;
  cancellationCost?: number | null;
  hoursBeforeCancellingCosts?: number | null;
  cancellationFeePctWithin24h?: number | null;
  cancellationFeePctWithin48h?: number | null;
};
type CancellationFeeSegment = {
  id?: string;
  service?: CancellationFeeService | null;
};
type TitleOnlyCancellationEvent = {
  title?: string | null;
  recurringEventId?: string;
  segments?: CancellationFeeSegment[];
  fees?: AppointmentFees | null;
};
export type AppointmentCancellationEvent = EventTiny | TitleOnlyCancellationEvent;
const isFullEventTiny = (event: AppointmentCancellationEvent): event is EventTiny => "type" in event && "start" in event && "attendees" in event && "segments" in event;
const hasServiceCancellationFeeConfigured = (service?: CancellationFeeService | null): boolean => {
  if (!service) return false;
  const hasPercentageFee = (service.cancellationFeePctWithin24h ?? 0) > 0 || (service.cancellationFeePctWithin48h ?? 0) > 0;
  const hasFixedFee = (service.cancellationCost ?? 0) > 0 && (service.hoursBeforeCancellingCosts ?? 0) > 0;
  return hasPercentageFee || hasFixedFee;
};
const getSegmentService = (segment: EventTiny["segments"][number] | CancellationFeeSegment): CancellationFeeService | null | undefined => "service" in segment ? segment.service : undefined;
const hasEventCancellationFeeConfigured = (event?: AppointmentCancellationEvent): boolean => event?.segments?.some(segment => hasServiceCancellationFeeConfigured(getSegmentService(segment))) ?? false;
const getFeeToggleCaption = ({
  shouldCharge,
  feeAmount,
  feeIsKnowable,
  autoChargeCardOnFile
}: {
  shouldCharge: boolean;
  feeAmount: string | null;
  feeIsKnowable: boolean;
  autoChargeCardOnFile: boolean;
}): string => {
  if (!shouldCharge) return "No cancellation fee will be charged";
  if (feeAmount) {
    return autoChargeCardOnFile ? `${feeAmount} will be charged to the card on file` : `${feeAmount} cancellation fee will be added`;
  }
  if (feeIsKnowable) return "No cancellation fee applies to this appointment";
  return autoChargeCardOnFile ? "Card on file will be charged if applicable" : "Fee will be charged if applicable";
};
interface Props {
  isOpen?: boolean;
  setIsOpen?: (val: boolean) => void;
  onConfirm: (reason: string, sendSms: boolean, cancellationMessage?: string, recurrenceModificationMode?: RecurrenceModificationMode, chargeCancellationFee?: boolean) => void | Promise<void>;
  event?: AppointmentCancellationEvent;
  children?: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  actionType?: "cancel" | "delete";
  defaultCancellationMessage?: string;
  /**
   * True when `defaultCancellationMessage` was composed from the service's
   * configured Cancellation SMS Template. An unedited message is then sent as
   * `undefined` so the backend hydrates that template itself, resolving tokens
   * the console can't (`{forms}`, `{portal_url}`, …). Staff edits are still
   * sent verbatim.
   */
  defaultCancellationMessageFromTemplate?: boolean;
  defaultCancellationReason?: string;
  hideSmsToggle?: boolean;
  hideReasonInput?: boolean;
  showCancellationFeeToggle?: boolean;
  autoChargeCardOnFile?: boolean;
  /** Optional callback when user wants to cancel instead of delete */
  onCancelInstead?: () => void;
}
const AppointmentCancellationModal: React.FC<Props> = t0 => {
  const $ = _c(128);
  const {
    isOpen: controlledIsOpen,
    setIsOpen: setControlledIsOpen,
    onConfirm,
    event,
    children,
    disabled: t1,
    isLoading: t2,
    actionType: t3,
    defaultCancellationMessage: t4,
    defaultCancellationMessageFromTemplate: t5,
    defaultCancellationReason: t6,
    hideSmsToggle: t7,
    hideReasonInput: t8,
    showCancellationFeeToggle: t9,
    autoChargeCardOnFile: t10,
    onCancelInstead
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const actionType = t3 === undefined ? "cancel" : t3;
  const defaultCancellationMessage = t4 === undefined ? "" : t4;
  const defaultCancellationMessageFromTemplate = t5 === undefined ? false : t5;
  const defaultCancellationReason = t6 === undefined ? "" : t6;
  const hideSmsToggle = t7 === undefined ? false : t7;
  const hideReasonInput = t8 === undefined ? false : t8;
  const showCancellationFeeToggle = t9 === undefined ? false : t9;
  const autoChargeCardOnFile = t10 === undefined ? false : t10;
  const getEventTitle = useEventTitle();
  let t11;
  if ($[0] !== event || $[1] !== getEventTitle) {
    t11 = event ? isFullEventTiny(event) ? getEventTitle(event) || "this appointment" : event.title?.trim() || "this appointment" : "this appointment";
    $[0] = event;
    $[1] = getEventTitle;
    $[2] = t11;
  } else {
    t11 = $[2];
  }
  const appointmentTitle = t11;
  const isRecurring = !!event?.recurringEventId;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState(defaultCancellationReason);
  const {
    sendCancellationMessage,
    setSendCancellationMessage
  } = useAppointmentNotificationStore();
  const [sendSms, setSendSms] = useState(sendCancellationMessage);
  const currentUser = useContext(UserContext)?.currentUser;
  let t12;
  if ($[3] !== currentUser) {
    t12 = hasPermission(currentUser, PERMISSIONS.SCHEDULING_CANCELLATION_FEE_SKIP);
    $[3] = currentUser;
    $[4] = t12;
  } else {
    t12 = $[4];
  }
  const canWaiveFee = t12;
  const [waiveRequested, setWaiveRequested] = useState(false);
  const shouldChargeCancellationFee = !(canWaiveFee && waiveRequested);
  const [cancellationMessage, setCancellationMessage] = useState(defaultCancellationMessage);
  const [recurrenceMode, setRecurrenceMode] = useState(RecurrenceModificationMode.THIS);
  const t13 = `Are you sure you want to cancel ${appointmentTitle}? This action cannot be undone.`;
  let t14;
  if ($[5] !== t13) {
    t14 = {
      title: "Cancel Appointment",
      description: t13,
      reasonLabel: "Reason for cancellation",
      reasonPlaceholder: "Please provide a reason for cancelling this appointment...",
      keepButton: "Keep Appointment",
      actionButton: "Cancel Appointment",
      loadingText: "Cancelling...",
      scopeLabel: "Which appointments would you like to cancel?"
    };
    $[5] = t13;
    $[6] = t14;
  } else {
    t14 = $[6];
  }
  const t15 = `Are you sure you want to delete ${appointmentTitle}? This action cannot be undone.`;
  let t16;
  if ($[7] !== t15) {
    t16 = {
      title: "Delete Appointment",
      description: t15,
      reasonLabel: "Reason for deletion",
      reasonPlaceholder: "Please provide a reason for deleting this appointment...",
      keepButton: "Keep Appointment",
      actionButton: "Delete Appointment",
      loadingText: "Deleting...",
      scopeLabel: "Which appointments would you like to delete?"
    };
    $[7] = t15;
    $[8] = t16;
  } else {
    t16 = $[8];
  }
  let t17;
  if ($[9] !== t14 || $[10] !== t16) {
    t17 = {
      cancel: t14,
      delete: t16
    };
    $[9] = t14;
    $[10] = t16;
    $[11] = t17;
  } else {
    t17 = $[11];
  }
  const actionText = t17[actionType];
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = setControlledIsOpen ?? setInternalIsOpen;
  let t18;
  if ($[12] !== actionType || $[13] !== event || $[14] !== showCancellationFeeToggle) {
    t18 = showCancellationFeeToggle && actionType === "cancel" && hasEventCancellationFeeConfigured(event);
    $[12] = actionType;
    $[13] = event;
    $[14] = showCancellationFeeToggle;
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  const shouldShowCancellationFeeToggle = t18;
  const quotedFees = actionType === "cancel" ? event?.fees : null;
  const feeIsKnowable = quotedFees != null;
  const feeAmount = quotedFees && quotedFees.cancellationFee > 0 ? formatCurrency(quotedFees.cancellationFee) : null;
  let t19;
  if ($[16] !== cancellationMessage || $[17] !== cancellationReason || $[18] !== defaultCancellationMessage || $[19] !== defaultCancellationMessageFromTemplate || $[20] !== defaultCancellationReason || $[21] !== hideReasonInput || $[22] !== isLoading || $[23] !== isRecurring || $[24] !== onConfirm || $[25] !== recurrenceMode || $[26] !== sendCancellationMessage || $[27] !== sendSms || $[28] !== shouldChargeCancellationFee || $[29] !== shouldShowCancellationFeeToggle) {
    t19 = () => {
      if (isLoading) {
        return;
      }
      const reason = hideReasonInput ? defaultCancellationReason : cancellationReason.trim();
      if (!hideReasonInput && !reason) {
        return;
      }
      const isUneditedTemplate = defaultCancellationMessageFromTemplate && cancellationMessage.trim() === defaultCancellationMessage.trim();
      onConfirm(reason, sendSms, sendSms && !isUneditedTemplate ? cancellationMessage.trim() || undefined : undefined, isRecurring ? recurrenceMode : undefined, shouldShowCancellationFeeToggle ? shouldChargeCancellationFee : undefined);
      setCancellationReason(defaultCancellationReason);
      setCancellationMessage(defaultCancellationMessage);
      setSendSms(sendCancellationMessage);
      setRecurrenceMode(RecurrenceModificationMode.THIS);
    };
    $[16] = cancellationMessage;
    $[17] = cancellationReason;
    $[18] = defaultCancellationMessage;
    $[19] = defaultCancellationMessageFromTemplate;
    $[20] = defaultCancellationReason;
    $[21] = hideReasonInput;
    $[22] = isLoading;
    $[23] = isRecurring;
    $[24] = onConfirm;
    $[25] = recurrenceMode;
    $[26] = sendCancellationMessage;
    $[27] = sendSms;
    $[28] = shouldChargeCancellationFee;
    $[29] = shouldShowCancellationFeeToggle;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const handleConfirm = t19;
  let t20;
  if ($[31] !== defaultCancellationMessage || $[32] !== defaultCancellationReason || $[33] !== sendCancellationMessage || $[34] !== setIsOpen) {
    t20 = () => {
      setCancellationReason(defaultCancellationReason);
      setCancellationMessage(defaultCancellationMessage);
      setSendSms(sendCancellationMessage);
      setWaiveRequested(false);
      setRecurrenceMode(RecurrenceModificationMode.THIS);
      setIsOpen(false);
    };
    $[31] = defaultCancellationMessage;
    $[32] = defaultCancellationReason;
    $[33] = sendCancellationMessage;
    $[34] = setIsOpen;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const handleCancel = t20;
  let t21;
  let t22;
  if ($[36] !== defaultCancellationMessage) {
    t21 = () => {
      setCancellationMessage(defaultCancellationMessage);
    };
    t22 = [defaultCancellationMessage];
    $[36] = defaultCancellationMessage;
    $[37] = t21;
    $[38] = t22;
  } else {
    t21 = $[37];
    t22 = $[38];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[39] !== defaultCancellationReason) {
    t23 = () => {
      setCancellationReason(defaultCancellationReason);
    };
    t24 = [defaultCancellationReason];
    $[39] = defaultCancellationReason;
    $[40] = t23;
    $[41] = t24;
  } else {
    t23 = $[40];
    t24 = $[41];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[42] !== isOpen || $[43] !== sendCancellationMessage) {
    t25 = () => {
      if (isOpen) {
        setSendSms(sendCancellationMessage);
      }
    };
    t26 = [isOpen, sendCancellationMessage];
    $[42] = isOpen;
    $[43] = sendCancellationMessage;
    $[44] = t25;
    $[45] = t26;
  } else {
    t25 = $[44];
    t26 = $[45];
  }
  useEffect(t25, t26);
  let t27;
  let t28;
  if ($[46] !== isOpen) {
    t27 = () => {
      if (isOpen) {
        setWaiveRequested(false);
      }
    };
    t28 = [isOpen];
    $[46] = isOpen;
    $[47] = t27;
    $[48] = t28;
  } else {
    t27 = $[47];
    t28 = $[48];
  }
  useEffect(t27, t28);
  let t29;
  let t30;
  if ($[49] !== isOpen) {
    t29 = () => {
      if (!isOpen) {
        setRecurrenceMode(RecurrenceModificationMode.THIS);
      }
    };
    t30 = [isOpen];
    $[49] = isOpen;
    $[50] = t29;
    $[51] = t30;
  } else {
    t29 = $[50];
    t30 = $[51];
  }
  useEffect(t29, t30);
  const T0 = Dialog;
  let t31;
  if ($[52] !== children) {
    t31 = children && <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[52] = children;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  const T1 = DialogContent;
  const t32 = "sm:max-w-lg";
  let t33;
  if ($[54] !== actionText.title) {
    t33 = <DialogTitle>{actionText.title}</DialogTitle>;
    $[54] = actionText.title;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== actionText.description) {
    t34 = <DialogDescription className="mt-1 text-body leading-6">{actionText.description}</DialogDescription>;
    $[56] = actionText.description;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  let t35;
  if ($[58] !== t33 || $[59] !== t34) {
    t35 = <DialogHeader>{t33}{t34}</DialogHeader>;
    $[58] = t33;
    $[59] = t34;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  const T2 = DialogBody;
  const t36 = "space-y-3";
  let t37;
  if ($[61] !== actionType || $[62] !== isLoading || $[63] !== onCancelInstead) {
    t37 = actionType === "delete" && onCancelInstead && <div className="rounded-md border border-status-warning-border bg-status-warning-bg p-3"><p className="text-body font-medium text-status-warning-text">This is different from cancelling</p><p className="mt-1 text-body text-foreground-secondary">Deleting removes the appointment from the system. To mark it as cancelled instead, use the button below.</p><Button type="button" variant="primary" size="sm" className="mt-2" onClick={onCancelInstead} disabled={isLoading}>Cancel Appointment Instead</Button></div>;
    $[61] = actionType;
    $[62] = isLoading;
    $[63] = onCancelInstead;
    $[64] = t37;
  } else {
    t37 = $[64];
  }
  let t38;
  if ($[65] !== actionText.scopeLabel || $[66] !== isLoading || $[67] !== isRecurring || $[68] !== recurrenceMode) {
    t38 = isRecurring && <div><Label>{actionText.scopeLabel}</Label><RecurrenceScopeSelector mode={recurrenceMode} onModeChange={setRecurrenceMode} isDisabled={isLoading} allowAllEvents={false} /></div>;
    $[65] = actionText.scopeLabel;
    $[66] = isLoading;
    $[67] = isRecurring;
    $[68] = recurrenceMode;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  const t39 = !shouldShowCancellationFeeToggle && feeAmount && <div data-testid="cancellation-fee-notice"><p className="text-body-strong text-foreground">Cancelling incurs a {feeAmount} cancellation fee.</p>{autoChargeCardOnFile && <p className="text-caption text-muted-foreground">This is charged at the time you cancel.</p>}</div>;
  let t40;
  if ($[70] !== actionText.reasonLabel || $[71] !== actionText.reasonPlaceholder || $[72] !== cancellationReason || $[73] !== hideReasonInput || $[74] !== isLoading) {
    t40 = !hideReasonInput && <div><Label htmlFor="cancellation-reason" mandatory={true}>{actionText.reasonLabel}</Label><Textarea id="cancellation-reason" placeholder={actionText.reasonPlaceholder} value={cancellationReason} onChange={e => setCancellationReason(e.target.value)} className="mt-1" rows={3} disabled={isLoading} /></div>;
    $[70] = actionText.reasonLabel;
    $[71] = actionText.reasonPlaceholder;
    $[72] = cancellationReason;
    $[73] = hideReasonInput;
    $[74] = isLoading;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== cancellationMessage || $[77] !== hideSmsToggle || $[78] !== isLoading || $[79] !== sendSms) {
    t41 = !hideSmsToggle && sendSms && <div className="space-y-2 border-t pt-3"><Label htmlFor="cancellation-message" className="text-body font-medium">Cancellation message</Label><p className="text-caption text-foreground-secondary">Customize the message that will be sent to the patient.</p><Textarea id="cancellation-message" value={cancellationMessage} onChange={e_0 => setCancellationMessage(e_0.target.value)} placeholder="Enter cancellation message..." rows={4} className="resize-none text-body" disabled={isLoading || !sendSms} /></div>;
    $[76] = cancellationMessage;
    $[77] = hideSmsToggle;
    $[78] = isLoading;
    $[79] = sendSms;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  const t42 = shouldShowCancellationFeeToggle && !canWaiveFee && <p data-testid="cancellation-fee-statement" className="border-t pt-3 text-body text-foreground">{getFeeStatement({
      feeLabel: "cancellation fee",
      feeAmount,
      feeIsKnowable,
      autoChargeCardOnFile
    })}</p>;
  const t43 = shouldShowCancellationFeeToggle && canWaiveFee && <div className="flex flex-col justify-between border-t pt-3 md:flex-row md:items-center"><div className="flex items-center space-x-2"><Checkbox id="chargeCancellationFee" checked={shouldChargeCancellationFee} onCheckedChange={checked => {
        setWaiveRequested(!checked);
      }} disabled={isLoading} /><Label htmlFor="chargeCancellationFee" className="cursor-pointer text-body-strong">Charge cancellation fee</Label></div><span className="text-caption text-muted-foreground">{getFeeToggleCaption({
        shouldCharge: shouldChargeCancellationFee,
        feeAmount,
        feeIsKnowable,
        autoChargeCardOnFile
      })}</span></div>;
  let t44;
  if ($[81] !== hideSmsToggle || $[82] !== isLoading || $[83] !== sendSms || $[84] !== setSendCancellationMessage || $[85] !== shouldShowCancellationFeeToggle) {
    t44 = !hideSmsToggle && <div className={cx("flex flex-col justify-between pt-3 md:flex-row md:items-center", !shouldShowCancellationFeeToggle && "border-t")}><div className="flex items-center space-x-2"><Checkbox id="sendSms" checked={sendSms} onCheckedChange={checked_0 => {
          const value = !!checked_0;
          setSendSms(value);
          setSendCancellationMessage(value);
        }} disabled={isLoading} /><Label htmlFor="sendSms" className="cursor-pointer text-body font-medium">Send cancellation message to patient</Label></div><span className="text-caption text-muted-foreground">{sendSms ? "Message will be sent" : "No message will be sent"}</span></div>;
    $[81] = hideSmsToggle;
    $[82] = isLoading;
    $[83] = sendSms;
    $[84] = setSendCancellationMessage;
    $[85] = shouldShowCancellationFeeToggle;
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  let t45;
  if ($[87] !== t37 || $[88] !== t38 || $[89] !== t39 || $[90] !== t40 || $[91] !== t41 || $[92] !== t42 || $[93] !== t43 || $[94] !== t44) {
    t45 = <div className={t36}>{t37}{t38}{t39}{t40}{t41}{t42}{t43}{t44}</div>;
    $[87] = t37;
    $[88] = t38;
    $[89] = t39;
    $[90] = t40;
    $[91] = t41;
    $[92] = t42;
    $[93] = t43;
    $[94] = t44;
    $[95] = t45;
  } else {
    t45 = $[95];
  }
  let t46;
  if ($[96] !== T2 || $[97] !== t45) {
    t46 = <T2>{t45}</T2>;
    $[96] = T2;
    $[97] = t45;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  const t47 = disabled || isLoading;
  let t48;
  if ($[99] !== actionText.keepButton || $[100] !== handleCancel || $[101] !== t47) {
    t48 = <DialogClose asChild={true}><Button variant="secondary" className="w-fit" onClick={handleCancel} disabled={t47}>{actionText.keepButton}</Button></DialogClose>;
    $[99] = actionText.keepButton;
    $[100] = handleCancel;
    $[101] = t47;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== cancellationReason || $[104] !== disabled || $[105] !== hideReasonInput || $[106] !== isLoading) {
    t49 = disabled || isLoading || !hideReasonInput && !cancellationReason.trim();
    $[103] = cancellationReason;
    $[104] = disabled;
    $[105] = hideReasonInput;
    $[106] = isLoading;
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  let t50;
  if ($[108] !== actionText.actionButton || $[109] !== actionText.loadingText || $[110] !== handleConfirm || $[111] !== isLoading || $[112] !== t49) {
    t50 = <Button className="w-fit" onClick={handleConfirm} variant="destructive" disabled={t49} isLoading={isLoading} loadingText={actionText.loadingText}>{actionText.actionButton}</Button>;
    $[108] = actionText.actionButton;
    $[109] = actionText.loadingText;
    $[110] = handleConfirm;
    $[111] = isLoading;
    $[112] = t49;
    $[113] = t50;
  } else {
    t50 = $[113];
  }
  let t51;
  if ($[114] !== t48 || $[115] !== t50) {
    t51 = <DialogFooter>{t48}{t50}</DialogFooter>;
    $[114] = t48;
    $[115] = t50;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  let t52;
  if ($[117] !== T1 || $[118] !== t35 || $[119] !== t46 || $[120] !== t51) {
    t52 = <T1 className={t32}>{t35}{t46}{t51}</T1>;
    $[117] = T1;
    $[118] = t35;
    $[119] = t46;
    $[120] = t51;
    $[121] = t52;
  } else {
    t52 = $[121];
  }
  let t53;
  if ($[122] !== T0 || $[123] !== isOpen || $[124] !== setIsOpen || $[125] !== t31 || $[126] !== t52) {
    t53 = <T0 open={isOpen} onOpenChange={setIsOpen}>{t31}{t52}</T0>;
    $[122] = T0;
    $[123] = isOpen;
    $[124] = setIsOpen;
    $[125] = t31;
    $[126] = t52;
    $[127] = t53;
  } else {
    t53 = $[127];
  }
  return t53;
};
export default AppointmentCancellationModal;